(() => {
  'use strict';

  if (window.PWFreestandingDishwasherFixLoaded) return;
  window.PWFreestandingDishwasherFixLoaded = true;

  const SERVICE_HANDLE = 'inbouwen-vrijstaande-vaatwasser-onder-aanrechtblad';
  const FALLBACK_VARIANT_ID = '47167386714447';
  const FALLBACK_PRICE = 7500;
  let contextPromise;

  const root = () => window.Shopify?.routes?.root || '/';

  const money = (cents) => {
    const amount = Number(cents || 0) / 100;
    return Number.isInteger(amount)
      ? `€${amount.toLocaleString('nl-NL')},-`
      : `€${amount.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleFromPath = () => {
    const match = location.pathname.match(/\/products\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]) : '';
  };

  const fetchProduct = async (handle) => {
    if (!handle) return null;
    const response = await fetch(`${root()}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  };

  const normaliseTags = (tags) => {
    if (Array.isArray(tags)) return tags;
    if (typeof tags === 'string') return tags.split(',');
    return [];
  };

  const isFreestandingDishwasher = (product) => {
    if (!product) return false;
    const text = [product.title, product.type, ...normaliseTags(product.tags)]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    const dishwasher = text.includes('vaatwasser')
      || text.includes('afwasmachine')
      || text.includes('dishwasher');
    const freestanding = text.includes('vrijstaand')
      || text.includes('freestanding')
      || text.includes('free standing');
    const builtIn = text.includes('inbouw')
      || text.includes('integreerbaar')
      || text.includes('geïntegreerd')
      || text.includes('geintegreerd')
      || text.includes('onderbouw');

    return dishwasher && (freestanding || !builtIn);
  };

  const loadContext = () => {
    if (contextPromise) return contextPromise;

    contextPromise = Promise.allSettled([
      fetchProduct(handleFromPath()),
      fetchProduct(SERVICE_HANDLE)
    ]).then(([productResult, serviceResult]) => {
      const product = productResult.status === 'fulfilled' ? productResult.value : null;
      const service = serviceResult.status === 'fulfilled' ? serviceResult.value : null;
      const variant = service?.variants?.find((item) => item.available) || service?.variants?.[0];

      return {
        active: isFreestandingDishwasher(product),
        variantId: String(variant?.id || FALLBACK_VARIANT_ID),
        priceCents: Number(variant?.price || FALLBACK_PRICE)
      };
    }).catch(() => ({
      active: false,
      variantId: FALLBACK_VARIANT_ID,
      priceCents: FALLBACK_PRICE
    }));

    return contextPromise;
  };

  const ensureCard = (form, context) => {
    let input = form.querySelector('.pw-service-input[data-pw-service-group="installation"]');
    let card = input?.closest('.pw-service-card');

    if (!card) {
      const extras = form.querySelector('.pw-services--extras');
      if (!extras) return null;

      const group = document.createElement('div');
      group.className = 'pw-service-group pw-service-group--dishwasher-placement';
      const grid = document.createElement('div');
      grid.className = 'pw-service-grid';
      card = document.createElement('label');
      card.className = 'pw-service-card';
      card.innerHTML = '<input class="pw-service-input" type="checkbox" data-pw-service-group="installation"><span class="pw-service-card__body"><strong></strong><span></span></span><span class="pw-service-price"></span>';
      grid.appendChild(card);
      group.appendChild(grid);
      extras.prepend(group);
      input = card.querySelector('.pw-service-input');
    }

    input.dataset.pwServiceVariant = context.variantId;
    input.dataset.pwServiceTitle = 'Plaatsing vrijstaande vaatwasser onder aanrechtblad';
    input.dataset.pwInstallationKind = 'freestanding-dishwasher';
    input.value = context.variantId;

    const title = card.querySelector('.pw-service-card__body strong');
    const description = card.querySelector('.pw-service-card__body span');
    const price = card.querySelector('.pw-service-price');
    if (title) title.textContent = 'Plaatsing onder aanrechtblad';
    if (description) description.textContent = 'Niet inbegrepen bij de standaardbezorging. Wij plaatsen de vrijstaande vaatwasser onder een bestaand aanrechtblad; controleer vooraf de maatvoering.';
    if (price) price.textContent = money(context.priceCents);

    const extras = card.closest('.pw-services--extras');
    if (extras) {
      extras.hidden = false;
      const heading = extras.querySelector('.pw-section__header h2');
      if (heading) heading.textContent = 'Aansluiten, plaatsen en accessoires';
    }

    return card;
  };

  const updateCopy = (form, context) => {
    ensureCard(form, context);

    const choice = form.querySelector('.pw-product-service-choice');
    if (choice) {
      const headerTitle = choice.querySelector('.pw-product-service-choice__header strong');
      const headerText = choice.querySelector('.pw-product-service-choice__header span');
      if (headerTitle) headerTitle.textContent = 'Bezorging';
      if (headerText) headerText.textContent = 'Kies hoe wij bezorgen. Plaatsing onder een aanrechtblad kiest u apart.';

      const indoor = choice.querySelector('[data-pw-product-service-level][value="standard_installation"]')?.closest('.pw-product-service-level');
      const indoorTitle = indoor?.querySelector('.pw-product-service-level__body strong');
      const indoorText = indoor?.querySelector('.pw-product-service-level__body span');
      if (indoorTitle) indoorTitle.textContent = 'Bezorging binnenshuis';
      if (indoorText) indoorText.textContent = 'Wij brengen het apparaat naar de gewenste plek op de begane grond. Plaatsing onder een aanrechtblad is niet inbegrepen.';

      const threshold = choice.querySelector('[data-pw-product-service-level][value="threshold"]')?.closest('.pw-product-service-level');
      const thresholdTitle = threshold?.querySelector('.pw-product-service-level__body strong');
      const thresholdText = threshold?.querySelector('.pw-product-service-level__body span');
      if (thresholdTitle) thresholdTitle.textContent = 'Drempellevering';
      if (thresholdText) thresholdText.textContent = 'Tot aan de voordeur op de begane grond. Wij brengen het apparaat niet naar binnen.';

      const summary = choice.querySelector('details.pw-buybox-options > summary > span');
      if (summary) summary.textContent = 'Plaatsing en extra opties';
    }

    const details = form.querySelector('details.pw-buybox-options');
    if (details) {
      const count = [...details.querySelectorAll('.pw-service-input')]
        .filter((input) => input.dataset.pwServiceVariant && !input.closest('[hidden]')).length;
      const hint = details.querySelector('.pw-buybox-options__hint');
      if (hint) hint.textContent = count === 1 ? '1 optie' : `${count} opties`;
      details.hidden = count === 0;
    }

    const note = form.querySelector('[data-pw-buybox-service-note]');
    if (note) {
      note.textContent = `Bezorging kost €20,- en wordt bij het afrekenen berekend; afhalen is ook mogelijk. Plaatsing onder een aanrechtblad is niet inbegrepen. Plaatsing onder aanrechtblad: ${money(context.priceCents)}.`;
    }
  };

  const apply = async (rootNode = document) => {
    const context = await loadContext();
    if (!context.active) return;
    rootNode.querySelectorAll('form.pw-product-form[action*="/cart/add"]').forEach((form) => updateCopy(form, context));
  };

  const schedule = (rootNode = document) => setTimeout(() => apply(rootNode), 0);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => apply(document), { once: true });
  } else {
    apply(document);
  }

  document.addEventListener('change', (event) => {
    if (event.target?.closest('form.pw-product-form')) schedule(document);
  }, true);
  document.addEventListener('shopify:section:load', (event) => schedule(event.target));
  document.addEventListener('on:cart:change', () => schedule(document));
})();
